import React, { Component } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { Collapse, FormGroup, Input, Label } from 'reactstrap';

import commonHelper from '@das/canvas-common/src/helper';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import ContextMenu from 'app/ContextMenu/components/ContextMenu';
import DragAndDropList from 'app/DragAndDropList/container';
import EditComponentModal from 'app/EditComponentModal/components/EditComponentModal';
import History from 'app/History/components/History';
import ImageSize from 'app/ImageSize/components/ImageSize';
import NameAndTags from 'app/NameAndTags/components/NameAndTags';
import PageButtonsList from 'app/PageButtonsList/container';
import { EMPTY_COMPONENT_PAYLOAD, ID, IFRAME_MESSAGE } from 'app/PreviewPane/const';
import { PAGE_TYPES } from 'app/RenderingEnvironment/const';

import sharedStyles from 'app/shared.css';
import { NAME_TAGS_LOCATION, PAGE_NAME_LOCATION, USER_TAGS_LOCATION } from 'lib/const';
import { EN } from 'lib/locales';
import { noop } from 'lib/noop';
import { SectionHeader } from 'ui/Components';
import PrimaryButton from 'ui/Components/Buttons/PrimaryButton';
import { renderArrowStyle } from 'ui/helpers';
import { API_COMPONENT_ACTION_ADDED } from 'utils/factory';

import AddComponents from './AddComponents';
import PublishOptions from './PublishOptions';
import styles from './PageComponentsList.css';

const REMOVE = 'REMOVE';
const EDIT = 'EDIT';

export const componentActions = [
  { text: 'Remove', action: REMOVE },
  { text: 'Edit', action: EDIT }
];

/**
 * render a pane for pages
 * displays a component list and node information
 */
export default class PageComponentsList extends Component {
  static propTypes = {
    addFilter: propTypes.func.isRequired,
    addNewComponentToPage: propTypes.func.isRequired,
    categoryId: propTypes.string.isRequired,
    changePagePropertyDetail: propTypes.func.isRequired,
    checkStatusAndSwitchToComponent: propTypes.func.isRequired,
    clearComponentUsage: propTypes.func.isRequired,
    clearSearch: propTypes.func.isRequired,
    componentHovering: propTypes.object,
    componentSelected: propTypes.object,
    componentsList: propTypes.array,
    contentLinkDisabled: propTypes.bool,
    editComponentOnPage: propTypes.func.isRequired,
    flashEventPageCategory: propTypes.string,
    flashEventPageRunAsOf: propTypes.string,
    getComponentUsage: propTypes.func.isRequired,
    handleComponentSelection: propTypes.func.isRequired,
    handleComponentDeselection: propTypes.func.isRequired,
    handleComponentHoverEnter: propTypes.func.isRequired,
    handleComponentHoverLeave: propTypes.func.isRequired,
    headline: propTypes.string.isRequired,
    isCompassPage: propTypes.bool,
    isFavorite: propTypes.bool,
    isPageSelected: propTypes.bool,
    isReadonly: propTypes.bool,
    mwpIFrameRef: propTypes.object.isRequired,
    node: propTypes.array.isRequired,
    notifySuccess: propTypes.func.isRequired,
    onListChange: propTypes.func.isRequired,
    pageComponents: propTypes.arrayOf(propTypes.shape),
    pagePayload: propTypes.object.isRequired,
    pageTemplateName: propTypes.string.isRequired,
    pageType: propTypes.string.isRequired,
    previewMetadata: propTypes.object,
    renderingEnvName: propTypes.string,
    replaceComponentWithACopy: propTypes.func.isRequired,
    resyncFlashEventLP: propTypes.func,
    routeMapping: propTypes.array.isRequired,
    seoSettings: propTypes.object.isRequired,
    shouldRenderToggleButton: propTypes.func.isRequired,
    showPane: propTypes.bool.isRequired,
    toggleFavoriteContent: propTypes.func.isRequired,
    unboundVisitComponent: propTypes.func.isRequired,
    unboundVisitPage: propTypes.func.isRequired,
    unsupportedComponentList: propTypes.arrayOf(propTypes.string)
  };

  static defaultProps = {
    componentHovering: EMPTY_COMPONENT_PAYLOAD,
    componentSelected: EMPTY_COMPONENT_PAYLOAD,
    componentsList: [],
    contentLinkDisabled: true,
    flashEventPageCategory: '',
    flashEventPageRunAsOf: '',
    pageComponents: [],
    previewMetadata: {},
    isCompassPage: null,
    isFavorite: false,
    isPageSelected: false,
    isReadonly: true,
    renderingEnvName: '',
    resyncFlashEventLP: noop,
    unsupportedComponentList: []
  };

  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
      selectedComponentIndex: undefined,
      showComponentsList: true,
      showPageInformation: true,
      showHistorySection: false,
      showYesNoConfirmation: false,
      showImageSizeSection: false
    };

    this.cancelClick = this.cancelClick.bind(this);
    this.changeItemsOrder = this.changeItemsOrder.bind(this);
    this.createACopyClicked = this.createACopyClicked.bind(this);
    this.editOriginalClicked = this.editOriginalClicked.bind(this);
    this.handleComponentsListClick = this.handleComponentsListClick.bind(this);
    this.handleHistorySectionClick = this.handleHistorySectionClick.bind(this);
    this.handlePageInformationClick = this.handlePageInformationClick.bind(this);
    this.handleWeightSectionClick = this.handleWeightSectionClick.bind(this);
    this.hideYesNoConfirmation = this.hideYesNoConfirmation.bind(this);
    this.onActionSelect = this.onActionSelect.bind(this);
    this.removeComponent = this.removeComponent.bind(this);
    this.resyncFlashEventLPClick = this.resyncFlashEventLPClick.bind(this);
    this.selectingComponent = this.selectingComponent.bind(this);
    this.componentHoverEnter = this.componentHoverEnter.bind(this);
    this.componentHoverLeave = this.componentHoverLeave.bind(this);
    this.showRemoveComponentConfirmation = this.showRemoveComponentConfirmation.bind(this);
  }

  /**
   * toggles page information section
   */
  handlePageInformationClick() {
    this.setState(ps => ({ showPageInformation: !ps.showPageInformation }));
  }

  /**
   * toggles components list section
   */
  handleComponentsListClick() {
    this.setState(ps => ({ showComponentsList: !ps.showComponentsList }));
  }

  /**
   * Toggle history section click
   */
  handleHistorySectionClick() {
    this.setState(ps => ({ showHistorySection: !ps.showHistorySection }));
  }

  /**
   * Toggle weight section click
   */
  handleWeightSectionClick() {
    this.setState(ps => ({ showImageSizeSection: !ps.showImageSizeSection }));
  }

  /**
   * toggles visible sections
   * @param {string} action - selected action
   * @param {string} index - index of the item on which action is selected
   */
  onActionSelect(action, index) {
    const { pagePayload, editComponentOnPage, getComponentUsage } = this.props;
    this.setState({ selectedComponentIndex: index });

    switch (action) {
      case REMOVE: {
        this.showRemoveComponentConfirmation();
        break;
      }
      case EDIT: {
        const { Id, IsMaster, UsedInMultiplePlaces } = pagePayload.Payload.Components[index];
        if (!IsMaster && !UsedInMultiplePlaces) {
          editComponentOnPage({ index });
        } else {
          getComponentUsage(Id);
          this.toggleEditComponentModal();
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * get schema name of current component
   * @returns {string} schema name
   */
  get showFavoriteIcon() {
    const { pagePayload } = this.props;
    return !(pagePayload.Action === API_COMPONENT_ACTION_ADDED);
  }

  /**
   * dispatches required actions for Edit original clicked.
   */
  editOriginalClicked() {
    const { selectedComponentIndex } = this.state;
    const {
      pagePayload: {
        Payload: { Components }
      }
    } = this.props;
    const component = Components[selectedComponentIndex];
    const { clearComponentUsage, checkStatusAndSwitchToComponent } = this.props;
    this.toggleEditComponentModal();
    clearComponentUsage();
    checkStatusAndSwitchToComponent(component.Id, component.Version);
  }

  /**
   * dispatches required actions to create a copy.
   */
  createACopyClicked() {
    const { selectedComponentIndex } = this.state;
    const {
      clearComponentUsage,
      editComponentOnPage,
      pagePayload: {
        Payload: { Components }
      },
      replaceComponentWithACopy
    } = this.props;
    const component = Components[selectedComponentIndex];
    this.toggleEditComponentModal();
    clearComponentUsage();
    replaceComponentWithACopy({ index: selectedComponentIndex, component });

    // Id of the component has changed change after replacement above
    editComponentOnPage({ index: selectedComponentIndex });
  }

  /**
   * toggles the modal and dispatches action to clear usage data
   */
  cancelClick() {
    const { clearComponentUsage } = this.props;
    this.toggleEditComponentModal();
    clearComponentUsage();
  }

  /**
   * resyncs flash event landing page
   */

  resyncFlashEventLPClick() {
    const {
      flashEventPageRunAsOf,
      flashEventPageCategory,
      pagePayload,
      resyncFlashEventLP
    } = this.props;
    const flashLPPayload = {
      cwd: pagePayload.ParentFolderId,
      flashEventPageRunAsOf,
      pageCategory: flashEventPageCategory,
      pageName: pagePayload.PageName,
      pageId: pagePayload.Id
    };
    resyncFlashEventLP(flashLPPayload);
  }

  /**
   * toggles component usage modal
   */
  toggleEditComponentModal() {
    this.setState(ps => ({ isModalOpen: !ps.isModalOpen }));
  }

  /**
   * removes selected component from the page
   * @param {string} index - index of the item on which remove action is selected
   */
  removeComponent(index) {
    const { pagePayload, onListChange, notifySuccess } = this.props;
    const newList = [...pagePayload.Payload.Components];
    const removedComponent = newList.splice(index, 1)[0];
    onListChange(newList);
    this.hideYesNoConfirmation();
    notifySuccess(`Component '${removedComponent.ComponentName}' is removed from the Page`);
  }

  /**
   * shows yes/no confirmation modal for remove component
   */
  showRemoveComponentConfirmation() {
    this.setState({ showYesNoConfirmation: true });
  }

  /**
   * hide yes no confirmation modal
   */
  hideYesNoConfirmation() {
    this.setState({ showYesNoConfirmation: false });
  }

  /**
   * Repositions the items in the array
   * @param {number} dragIndex - index number of the dragged item in the array
   * @param {number} hoverIndex - index number of the item dragged to in the array
   */
  changeItemsOrder(dragIndex, hoverIndex) {
    const { pagePayload } = this.props;
    const items = pagePayload.Payload.Components;
    const componentList = [...items];
    const dragCard = componentList[dragIndex];
    componentList.splice(dragIndex, 1);
    componentList.splice(hoverIndex, 0, dragCard);
    this.props.onListChange(componentList);
  }

  /**
   * Store selected component data in state and send to MWP iframe
   * @param {object} component - selected component
   */
  selectingComponent(component) {
    const { mwpIFrameRef, componentSelected } = this.props;

    // if the selected component is the same as the component in state, de-select component
    if (
      component.Id === componentSelected.Id ||
      component.variantIds.includes(componentSelected.Id) ||
      component.childComponentIds.includes(componentSelected.Id)
    ) {
      this.props.handleComponentDeselection();
      mwpIFrameRef.current.contentWindow.postMessage(
        {
          msg: {
            payload: EMPTY_COMPONENT_PAYLOAD,
            type: IFRAME_MESSAGE.DESELECTING
          },
          type: IFRAME_MESSAGE.CANVAS
        },
        '*'
      );
    } else {
      this.props.handleComponentSelection(component);
      mwpIFrameRef.current.contentWindow.postMessage(
        {
          msg: {
            payload: component,
            type: IFRAME_MESSAGE.SELECTING
          },
          type: IFRAME_MESSAGE.CANVAS
        },
        '*'
      );
    }
  }

  /**
   * Store data for component on hover enter and send to MWP iframe
   * @param {object} component - component on hover enter
   */
  componentHoverEnter(component) {
    this.props.handleComponentHoverEnter(component);
    this.props.mwpIFrameRef.current.contentWindow.postMessage(
      {
        msg: {
          payload: component,
          type: IFRAME_MESSAGE.HOVER_ENTER
        },
        type: IFRAME_MESSAGE.CANVAS
      },
      '*'
    );
  }

  /**
   * Store data for component on hover leave and send to MWP iframe
   */
  componentHoverLeave() {
    this.props.handleComponentHoverLeave();
    this.props.mwpIFrameRef.current.contentWindow.postMessage(
      {
        msg: {
          payload: EMPTY_COMPONENT_PAYLOAD,
          type: IFRAME_MESSAGE.HOVER_LEAVE
        },
        type: IFRAME_MESSAGE.CANVAS
      },
      '*'
    );
  }

  /**
   * renders list of components for active page
   * @returns {Component} div
   */
  renderComponentsList() {
    const {
      componentHovering,
      componentSelected,
      contentLinkDisabled,
      isReadonly,
      pagePayload: { Payload: { Components: components = [] } = {} } = {},
      previewMetadata,
      unsupportedComponentList
    } = this.props;

    if (!components.length) {
      return 'There are no components on this page.';
    }

    const list = components.map((component, index) => {
      return (
        <div
          id={ID.COMPONENT_LIST}
          key={component.Id}
          className={classnames([
            unsupportedComponentList.includes(component.Id) && styles.unsupportedComponent,
            'd-flex justify-content-between'
          ])}
          variantids={
            component.Payload?.variants
              ? component.Payload.variants.map(variant => variant.data.Id)
              : []
          }
          childids={
            component.Payload?.components
              ? component.Payload.components.map(comp => comp.Id)
              : []
          }
        >
          <span
            id={ID.COMPONENT_LIST}
            className="d-inline-block text-truncate"
            title={component.ComponentName}
          >
            {`${index + 1}. ${component.ComponentName}`}
          </span>
          {!isReadonly && (
            <ContextMenu
              id={ID.CONTEXT_MENU}
              actions={componentActions}
              onActionSelect={action => this.onActionSelect(action, index)}
              popoverPlacement="left"
            />
          )}
        </div>
      );
    });

    return isReadonly ? (
      list
    ) : (
      <DragAndDropList
        cardClassName="px-2 py-1 mb-1"
        changeItemsOrder={this.changeItemsOrder}
        componentHoverEnter={this.componentHoverEnter}
        componentHovering={componentHovering}
        componentHoverLeave={this.componentHoverLeave}
        componentSelected={componentSelected}
        contentLinkDisabled={contentLinkDisabled}
        handleComponentSelection={this.selectingComponent}
        previewMetadata={previewMetadata}
      >
        {list}
      </DragAndDropList>
    );
  }

  /**
   * renders modal for remove component confirmation.
   * @returns {Component} component to show a modal for confirming remove component action.
   */
  renderYesNoConfirmationModal() {
    const { selectedComponentIndex, showYesNoConfirmation } = this.state;
    const {
      pagePayload: {
        Payload: { Components }
      }
    } = this.props;
    const selectedComponent = Components[selectedComponentIndex] || {};
    return (
      <ConfirmationModal
        isOpen={showYesNoConfirmation}
        onNoClick={this.hideYesNoConfirmation}
        onYesClick={() => this.removeComponent(selectedComponentIndex)}
        data-test-id="pageComponentList-yesNoConfirmationModal"
      >
        <p>Are you sure, you want to remove this component from page?</p>
        <strong>{selectedComponent.ComponentName}</strong>
      </ConfirmationModal>
    );
  }

  /**
   * renders modal for Edit component.
   * @returns {Component} component to show a modal on Edit Component click
   */
  renderEditComponentModal() {
    const { unboundVisitComponent, unboundVisitPage } = this.props;
    const { isModalOpen } = this.state;
    return (
      <EditComponentModal
        data-test-id="pageComponentsList-editComponentModal"
        isOpen={isModalOpen}
        onCancelClick={this.cancelClick}
        onEditOriginalClick={this.editOriginalClicked}
        onEditCopyClick={this.createACopyClicked}
        unboundVisitComponent={unboundVisitComponent}
        unboundVisitPage={unboundVisitPage}
      />
    );
  }

  render() {
    const {
      addFilter,
      pageComponents,
      addNewComponentToPage,
      categoryId,
      changePagePropertyDetail,
      clearSearch,
      componentsList,
      flashEventPageCategory,
      flashEventPageRunAsOf,
      headline,
      isCompassPage,
      isFavorite,
      isPageSelected,
      isReadonly,
      node,
      pagePayload,
      pageTemplateName,
      pageType,
      renderingEnvName,
      routeMapping,
      seoSettings,
      showPane,
      shouldRenderToggleButton,
      toggleFavoriteContent
    } = this.props;

    const {
      showComponentsList,
      showHistorySection,
      showImageSizeSection,
      showPageInformation
    } = this.state;

    // FIXME refactor when combining NameAndTags implementations
    const pageSchema = {
      name: PAGE_NAME_LOCATION,
      nameTags: NAME_TAGS_LOCATION,
      userTags: USER_TAGS_LOCATION
    };
    const IsDoNotArchiveField = 'IsDoNotArchive';

    const togglePaneStyle = showPane ? classnames('pane', styles.open) : styles.closed;

    shouldRenderToggleButton(isPageSelected);

    if (isPageSelected) {
      const contentSizes = commonHelper.calculateTotalContentSize(pagePayload);

      return (
        <div className={togglePaneStyle}>
          <div className={styles.wrapper}>
            <SectionHeader theme={styles}>Page Name & Tagging</SectionHeader>
            <Collapse isOpen>
              <NameAndTags
                key={pagePayload.Id}
                isFavorite={isFavorite}
                isReadonly={isReadonly}
                modify={changePagePropertyDetail}
                name={pagePayload.PageName}
                onFavoriteClick={() => {
                  return toggleFavoriteContent(
                    isFavorite,
                    pagePayload.PageName,
                    'page',
                    pagePayload.Id,
                    EN
                  );
                }}
                schema={pageSchema}
                showFavoriteIcon={this.showFavoriteIcon}
                tags={pagePayload.Tags}
              />
              <div className={`p-4 ${sharedStyles.borderTopDashedLight}`}>
                <FormGroup check inline>
                  <Label className="font-weight-bold">
                    <Input
                      disabled={isReadonly}
                      type="checkbox"
                      checked={pagePayload.IsDoNotArchive}
                      data-test-id="pageComponentsList-doNotArchiveCheckbox"
                      onChange={e => {
                        return changePagePropertyDetail(e.target.checked, IsDoNotArchiveField);
                      }}
                    />
                    Do Not Archive
                  </Label>
                </FormGroup>
              </div>
              {/* conditionally render resync button for flash event landing pages */}
              {pageType === PAGE_TYPES.FLASH_EVENT && (
                <div className={`p-4 ${sharedStyles.borderTopDashedLight}`}>
                  <PrimaryButton
                    onClick={this.resyncFlashEventLPClick}
                    disabled={!(flashEventPageCategory && flashEventPageRunAsOf)}
                    data-test-id="pageComponentsList-resyncFlashLPButton"
                  >
                    Resync Flash Event LP
                  </PrimaryButton>
                </div>
              )}
            </Collapse>
            <SectionHeader onClick={this.handleComponentsListClick}>
              Components List
              {renderArrowStyle(showComponentsList)}
            </SectionHeader>
            <Collapse isOpen={showComponentsList}>
              <div className="p-4">
                <AddComponents
                  addFilter={addFilter}
                  addNewComponentToPage={addNewComponentToPage}
                  clearSearch={clearSearch}
                  componentsList={componentsList}
                  isReadonly={isReadonly}
                  pageTemplateName={pageTemplateName}
                  pageType={pageType}
                  renderingEnvName={renderingEnvName}
                  pageComponents={pageComponents}
                />
                <div data-test-id="pageComponentsList-list" id={ID.COMPONENT_LIST}>
                  {this.renderComponentsList()}
                </div>
              </div>
            </Collapse>
            <SectionHeader onClick={this.handlePageInformationClick}>
              Page Information
              {renderArrowStyle(showPageInformation)}
            </SectionHeader>
            <Collapse isOpen={showPageInformation}>
              <PublishOptions
                categoryId={categoryId}
                changePagePropertyDetail={changePagePropertyDetail}
                headline={headline}
                isCompassPage={isCompassPage}
                isReadonly={isReadonly}
                node={node}
                pagePayload={pagePayload}
                routeMapping={routeMapping}
                seoSettings={seoSettings}
              />
            </Collapse>
            <SectionHeader onClick={this.handleWeightSectionClick}>
              Content Weight
              {renderArrowStyle(showImageSizeSection)}
            </SectionHeader>
            <Collapse isOpen={showImageSizeSection}>
              {showImageSizeSection && <ImageSize contentSizes={contentSizes} />}
            </Collapse>
            <SectionHeader onClick={this.handleHistorySectionClick}>
              Page History
              {renderArrowStyle(showHistorySection)}
            </SectionHeader>
            <Collapse isOpen={showHistorySection}>
              <History history={pagePayload.History} />
            </Collapse>
          </div>
          <PageButtonsList />
          {this.renderEditComponentModal()}
          {this.renderYesNoConfirmationModal()}
        </div>
      );
    }

    return <div data-test-id="page-pane-invalid-schema" />;
  }
}
